/**
 * Symphony GROUP CHAT, Phase 10b — the pure half of WhatsApp's message actions (spec /tmp/p10b/BRIEF.md): which
 * actions a row offers, reaction chips, the pin list and its banner, stars, a realtime/answered row merged in place,
 * the forward result, message info, and every refusal's copy. No React and no fetch. iOS and Android hold the same
 * rules; `group-chat-phase10b.test.ts` pins this one.
 *
 * The wire is common-lib's `runGroupChatMessageAction` (GroupChatMessageGateway.ts): a refusal is a typed `refusal`
 * on a 200, a transport failure is a retry.
 */
import { contentText, takesCaption } from './group-content';
import {
  ownerShortName,
  participantLabel,
  personById,
  resolveAuthor,
  type GroupMessageMeta,
  type GroupReaction,
  type GroupSummary,
  type MessageAuthor,
  type Translate,
} from './group-chat';
import { refusalKey, type GroupActionOutcome } from './group-chat-actions';
import { isMyRow, type GroupCapabilities } from './group-admin';

const isObject = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);
const str = (v: unknown): string | undefined => (typeof v === 'string' && v.length > 0 ? v : undefined);

/** symphony-bff's Phase 10b bounds (common-lib `GROUP_MESSAGE_LIMITS`). */
export const GROUP_MESSAGE_RULES = {
  editWindowMs: 15 * 60 * 1000,
  deleteWindowMs: 48 * 60 * 60 * 1000,
  maxPins: 3,
  maxForwardTargets: 5,
  starredPage: 20,
} as const;

/** The quick reaction row, in display order — the private chat's six (lib/message-reactions.ts). */
export const GROUP_QUICK_REACTIONS = ['👍', '❤️', '😂', '😮', '😢', '🙏'] as const;

// ─── Which actions a row offers ─────────────────────────────────────────────────────────────────────────────

export type GroupMessageAction = 'react' | 'reply' | 'forward' | 'copy' | 'edit' | 'pin' | 'unpin' | 'star' | 'unstar' | 'info' | 'delete' | 'report' | 'deleteForMe';

export interface ActionRow {
  id?: string;
  role?: string;
  content?: string;
  createdDate?: string;
  author?: MessageAuthor | null;
  attachments?: unknown[];
  groupPost?: unknown;
  groupEvent?: unknown;
  groupApproval?: unknown;
  maestroUnavailable?: unknown;
  groupMeta?: Pick<GroupMessageMeta, 'deleted' | 'infoEvent' | 'workStopped' | 'chainStopped' | 'membersAdded' | 'forwarded' | 'content'> | null;
}

export interface ActionContext {
  viewerId: string | undefined;
  caps: GroupCapabilities | null;
  pinned: boolean;
  starred: boolean;
  /** Read when the menu opens, so Edit disappears once its 15 minutes pass. */
  now: number;
}

const SERVER_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** A SYSTEM line (membership, settings, pin, work stopped …) or a row not yet stored: no menu at all. */
export function isMenuLess(row: ActionRow): boolean {
  if (!row.id || !SERVER_ID.test(row.id) || row.groupPost) return true;
  if (row.role === 'SYSTEM' || row.author?.kind === 'SYSTEM') return true;
  if (row.groupEvent || row.groupMeta?.infoEvent || row.groupMeta?.workStopped || row.groupMeta?.chainStopped || row.groupMeta?.membersAdded) return true;
  if (row.groupApproval || row.maestroUnavailable) return true;
  return !row.author;
}

const ageMs = (row: ActionRow, now: number): number => {
  const t = row.createdDate ? Date.parse(row.createdDate) : NaN;
  return Number.isFinite(t) ? now - t : Number.POSITIVE_INFINITY;
};
/** The words Copy takes and Edit starts from: a typed row's caption (Phase 10c), else the text. */
export const actionText = (row: ActionRow): string => (row.groupMeta?.content ? contentText(row.groupMeta.content) : row.content ?? '');
const hasText = (row: ActionRow) => !!actionText(row).trim();
/** The viewer's own post (a PERSON row), never their Maestro's. */
export const isMyPost = (row: ActionRow, viewerId: string | undefined) => !!viewerId && row.author?.kind === 'HUMAN' && row.author.principalMetaSiteId === viewerId;
/** A Maestro reply whose owner is the viewer. */
export const isMyMaestroRow = (row: ActionRow, viewerId: string | undefined) =>
  !!viewerId && row.author?.kind === 'MAESTRO' && (row.author.agentOwnerMetaSiteId ?? row.author.principalMetaSiteId) === viewerId;

/** Edit: the viewer's own text post, not forwarded, while they may send, within 15 minutes. */
export function canEdit(row: ActionRow, ctx: ActionContext): boolean {
  // Phase 10c: a typed row's words are its caption — a photo, video, document or GIF only (with or without one yet).
  const editable = row.groupMeta?.content ? takesCaption(row.groupMeta.content.type) : hasText(row);
  return isMyPost(row, ctx.viewerId) && !row.groupMeta?.forwarded && editable && ctx.caps?.canSendMessages !== false
    && ageMs(row, ctx.now) < GROUP_MESSAGE_RULES.editWindowMs;
}

/** "Delete for everyone": a moderator any time; the author (or their Maestro's row) within 48 hours. */
export function canDeleteForEveryone(row: ActionRow, ctx: ActionContext): boolean {
  if (row.groupMeta?.deleted) return false;
  if (ctx.caps?.canModerate) return true;
  return isMyRow(row, ctx.viewerId) && ageMs(row, ctx.now) < GROUP_MESSAGE_RULES.deleteWindowMs;
}

/** The Delete choice: "Delete for me" always, "Delete for everyone" when allowed. */
export function deleteChoices(row: ActionRow, ctx: ActionContext): ('forMe' | 'forEveryone')[] {
  return canDeleteForEveryone(row, ctx) ? ['forMe', 'forEveryone'] : ['forMe'];
}

/** The row's menu, in THE order all three surfaces draw it. */
export function messageActions(row: ActionRow, ctx: ActionContext): GroupMessageAction[] {
  if (row.groupMeta?.deleted) return row.id && SERVER_ID.test(row.id) ? ['deleteForMe'] : [];
  if (isMenuLess(row)) return [];
  const out: GroupMessageAction[] = ['react'];
  if (ctx.caps?.canSendMessages !== false) out.push('reply');
  out.push('forward');
  if (hasText(row)) out.push('copy');
  if (canEdit(row, ctx)) out.push('edit');
  if (ctx.caps?.canEditInfo) out.push(ctx.pinned ? 'unpin' : 'pin');
  out.push(ctx.starred ? 'unstar' : 'star');
  if (isMyPost(row, ctx.viewerId) || isMyMaestroRow(row, ctx.viewerId)) out.push('info');
  out.push('delete');
  if (!isMyRow(row, ctx.viewerId)) out.push('report');
  return out;
}

// ─── Reactions ───────────────────────────────────────────────────────────────────────────────────────────────

export interface ReactionChip {
  emoji: string;
  count: number;
  mine: boolean;
  metaSiteIds: string[];
}

/** Chips: grouped by emoji, the most reacted first, a tie by the earliest reaction. */
export function reactionChips(reactions: readonly GroupReaction[] | undefined, viewerId: string | undefined): ReactionChip[] {
  const groups = new Map<string, { chip: ReactionChip; first: number; order: number }>();
  (reactions ?? []).forEach((r, i) => {
    const at = r.date ? Date.parse(r.date) : NaN;
    const when = Number.isFinite(at) ? at : Number.POSITIVE_INFINITY;
    const g = groups.get(r.emoji);
    if (g) {
      g.chip.count += 1;
      g.chip.metaSiteIds.push(r.metaSiteId);
      if (r.metaSiteId === viewerId) g.chip.mine = true;
      g.first = Math.min(g.first, when);
    } else {
      groups.set(r.emoji, { chip: { emoji: r.emoji, count: 1, mine: !!viewerId && r.metaSiteId === viewerId, metaSiteIds: [r.metaSiteId] }, first: when, order: i });
    }
  });
  return [...groups.values()]
    .sort((a, b) => b.chip.count - a.chip.count || a.first - b.first || a.order - b.order)
    .map((g) => g.chip);
}

export function myReaction(reactions: readonly GroupReaction[] | undefined, viewerId: string | undefined): string | null {
  if (!viewerId) return null;
  return (reactions ?? []).find((r) => r.metaSiteId === viewerId)?.emoji ?? null;
}

/** What `react` sends for a pick: the same emoji as mine clears (""), anything else sets (replaces). */
export function reactEmojiFor(current: string | null, chosen: string): string {
  return current === chosen ? '' : chosen;
}

/** The optimistic reactions after the viewer sends `emoji` ("" = clear). */
export function withMyReaction(reactions: readonly GroupReaction[] | undefined, viewerId: string, emoji: string, now = new Date()): GroupReaction[] {
  const rest = (reactions ?? []).filter((r) => r.metaSiteId !== viewerId);
  return emoji ? [...rest, { metaSiteId: viewerId, emoji, date: now.toISOString() }] : rest;
}

export interface WhoReactedRow {
  metaSiteId: string;
  name: string;
  emoji: string;
  mine: boolean;
}

/** The who-reacted sheet: tabs "All {count}" + one per emoji (chip order); rows of the chosen tab, mine first. */
export function whoReacted(
  reactions: readonly GroupReaction[] | undefined,
  filter: string | null,
  summary: GroupSummary | null,
  t: Translate,
): { tabs: { emoji: string | null; count: number }[]; rows: WhoReactedRow[] } {
  const viewerId = summary?.viewerId;
  const list = reactions ?? [];
  const tabs = [{ emoji: null as string | null, count: list.length }, ...reactionChips(list, viewerId).map((c) => ({ emoji: c.emoji as string | null, count: c.count }))];
  const rows = list
    .filter((r) => filter === null || r.emoji === filter)
    .map((r): WhoReactedRow => {
      const mine = !!viewerId && r.metaSiteId === viewerId;
      const p = personById(summary, r.metaSiteId);
      return { metaSiteId: r.metaSiteId, emoji: r.emoji, mine, name: mine ? t('chat.group.you') : p ? participantLabel(p, t) : '' };
    });
  return { tabs, rows: [...rows.filter((r) => r.mine), ...rows.filter((r) => !r.mine)] };
}

/** A chip's accessible label: "👍 3, reacted by You, Dana, Amir". */
export function chipA11y(chip: ReactionChip, summary: GroupSummary | null, t: Translate): string {
  const names = chip.metaSiteIds.map((id) => {
    if (summary?.viewerId === id) return t('chat.group.you');
    const p = personById(summary, id);
    return p ? ownerShortName(p, t) : '';
  }).filter(Boolean);
  return t('chat.group.reactions.chipA11y', { emoji: chip.emoji, count: chip.count, names: names.join(', ') });
}

// ─── Pins ────────────────────────────────────────────────────────────────────────────────────────────────────

export interface GroupPin {
  messageId: string;
  /** The first line of the text; '' = an attachment only. */
  text: string;
  pinnedByMetaSiteId: string | null;
  pinnedDate: string | null;
}

const firstLine = (text: unknown): string => (typeof text === 'string' ? text.trim().split(/\r?\n/)[0]?.trim() ?? '' : '');

/** One pin off the wire — a window's `pinnedMessages[]` (light row) or an answer's (stored row): same fields. */
export function parsePin(raw: unknown): GroupPin | null {
  if (!isObject(raw) || !isObject(raw.message)) return null;
  const messageId = str(raw.message.id);
  if (!messageId) return null;
  return { messageId, text: firstLine(raw.message.content), pinnedByMetaSiteId: str(raw.pinnedByMetaSiteId) ?? null, pinnedDate: str(raw.pinnedDate) ?? null };
}

/** The pin list, oldest first, at most 3 (the newest kept), one per message. */
export function parsePins(raw: unknown): GroupPin[] {
  if (!Array.isArray(raw)) return [];
  const out: GroupPin[] = [];
  for (const r of raw) {
    const pin = parsePin(r);
    if (pin && !out.some((p) => p.messageId === pin.messageId)) out.push(pin);
  }
  return out.slice(-GROUP_MESSAGE_RULES.maxPins);
}

/** A window landing: the LATEST page's pins replace the list; an older page (or a read without pins) leaves it. */
export function pinsAfterWindow(current: GroupPin[], windowPins: unknown, latest: boolean): GroupPin[] {
  if (!latest || !Array.isArray(windowPins)) return current;
  return parsePins(windowPins);
}

interface PinRow {
  id?: string;
  content?: string;
  groupMeta?: Pick<GroupMessageMeta, 'pinned' | 'deleted'> | null;
}

/**
 * A realtime or answered row: gained `groupPinned` and not listed → appended (the oldest dropped beyond 3); lost it,
 * or became a tombstone → removed; still pinned and listed → its text refreshed. The same array when nothing changes.
 */
export function pinsAfterRow(current: GroupPin[], row: PinRow): GroupPin[] {
  if (!row.id) return current;
  const listed = current.findIndex((p) => p.messageId === row.id);
  const pinned = row.groupMeta?.pinned && !row.groupMeta.deleted ? row.groupMeta.pinned : null;
  if (!pinned) return listed < 0 ? current : current.filter((p) => p.messageId !== row.id);
  const pin: GroupPin = { messageId: row.id, text: firstLine(row.content), pinnedByMetaSiteId: pinned.pinnedByMetaSiteId, pinnedDate: pinned.pinnedDate };
  if (listed >= 0) {
    const was = current[listed];
    if (was.text === pin.text) return current;
    return current.map((p, i) => (i === listed ? { ...was, text: pin.text } : p));
  }
  return [...current, pin].slice(-GROUP_MESSAGE_RULES.maxPins);
}

export interface PinBanner {
  messageId: string;
  labelKey: 'chat.group.pinned.title' | 'chat.group.pinned.count';
  vars?: { index: number; count: number };
  /** The pin's first line; null → "Attachment". */
  text: string | null;
}

/**
 * The banner for cursor `cursor` (0 = the NEWEST pin; it is where the banner starts). "Pinned message" alone, or
 * "Pinned message {index} of {count}" with index 1 = the newest. Null with no pins.
 */
export function pinBanner(pins: readonly GroupPin[], cursor: number): PinBanner | null {
  if (pins.length === 0) return null;
  const c = ((cursor % pins.length) + pins.length) % pins.length;
  const pin = pins[pins.length - 1 - c];
  return {
    messageId: pin.messageId,
    labelKey: pins.length === 1 ? 'chat.group.pinned.title' : 'chat.group.pinned.count',
    ...(pins.length > 1 ? { vars: { index: c + 1, count: pins.length } } : {}),
    text: pin.text || null,
  };
}

/** After a tap the banner advances to the next OLDER pin, cycling back to the newest. */
export function nextPinCursor(cursor: number, count: number): number {
  return count > 0 ? (cursor + 1) % count : 0;
}

/** What a `pin` answer does: the list it leaves, whether to ask "Replace the oldest pin?", and its copy. */
export type PinResult =
  | { kind: 'done'; pins: GroupPin[]; toastKey: 'chat.group.message.pinnedToast' | 'chat.group.message.unpinnedToast' }
  | { kind: 'confirmReplace'; pins: GroupPin[] }
  | { kind: 'error'; pins: GroupPin[] | null; messageKey: string };
export function pinResult(outcome: GroupActionOutcome, pinned: boolean, replaceOldest: boolean): PinResult {
  if (outcome.kind === 'ok') {
    return { kind: 'done', pins: parsePins(outcome.data.pinnedMessages), toastKey: pinned ? 'chat.group.message.pinnedToast' : 'chat.group.message.unpinnedToast' };
  }
  if (outcome.kind === 'refused' && outcome.refusal === 'PIN_LIMIT_REACHED') {
    const pins = parsePins(outcome.data.pinnedMessages);
    return replaceOldest ? { kind: 'error', pins, messageKey: 'chat.group.error.pinLimitReached' } : { kind: 'confirmReplace', pins };
  }
  return { kind: 'error', pins: null, messageKey: messageActionErrorKey('pin', outcome) };
}

// ─── Stars ───────────────────────────────────────────────────────────────────────────────────────────────────

/** The starred set after a window (any page): its ids join what was already known. Same object when nothing is new. */
export function starredAfterWindow(current: ReadonlySet<string>, ids: unknown): ReadonlySet<string> {
  if (!Array.isArray(ids)) return current;
  const add = ids.filter((id): id is string => typeof id === 'string' && !!id && !current.has(id));
  if (add.length === 0) return current;
  return new Set([...current, ...add]);
}

export function withStar(current: ReadonlySet<string>, messageId: string, starred: boolean): ReadonlySet<string> {
  if (current.has(messageId) === starred) return current;
  const next = new Set(current);
  if (starred) next.add(messageId);
  else next.delete(messageId);
  return next;
}

export interface StarredItem {
  conversationId: string;
  groupName: string;
  messageId: string;
  author: MessageAuthor | null;
  /** The first lines; '' = an attachment only. */
  text: string;
  createdDate: string | null;
  starredDate: string | null;
}

/** One `starred` page: newest star first, plus the cursor of the next (null at the end). */
export function parseStarredPage(raw: unknown): { items: StarredItem[]; nextCursor: string | null } {
  if (!isObject(raw)) return { items: [], nextCursor: null };
  const items = (Array.isArray(raw.messages) ? raw.messages : []).flatMap((m): StarredItem[] => {
    if (!isObject(m) || !isObject(m.message)) return [];
    const conversationId = str(m.conversationId);
    const messageId = str(m.message.id);
    if (!conversationId || !messageId) return [];
    const a = isObject(m.message.author) ? m.message.author : null;
    const kind = a?.kind === 'HUMAN' || a?.kind === 'MAESTRO' || a?.kind === 'SYSTEM' ? a.kind : null;
    const principal = str(a?.principalMetaSiteId);
    const owner = str(a?.agentOwnerMetaSiteId);
    return [{
      conversationId,
      groupName: typeof m.groupName === 'string' ? m.groupName : '',
      messageId,
      author: kind && principal ? { principalMetaSiteId: principal, kind, ...(owner ? { agentOwnerMetaSiteId: owner } : {}) } : null,
      text: typeof m.message.content === 'string' ? m.message.content.trim() : '',
      createdDate: str(m.message.createdDate) ?? null,
      starredDate: str(m.starredDate) ?? null,
    }];
  });
  return { items, nextCursor: str(raw.nextCursor) ?? null };
}

/** The starred list after another page arrives (deduped by message; the first time a message shows wins). */
export function appendStarredPage(current: readonly StarredItem[], page: readonly StarredItem[]): StarredItem[] {
  const seen = new Set(current.map((i) => i.messageId));
  return [...current, ...page.filter((i) => !seen.has(i.messageId) && seen.add(i.messageId))];
}

/** "in {group}" — an unnamed group reads "Group chat". */
export function starredGroupLine(item: Pick<StarredItem, 'groupName'>, t: Translate): string {
  return t('chat.group.starred.inGroup', { group: item.groupName.trim() || t('chat.group.starred.unnamedGroup') });
}

/**
 * The starred row's times: the time it shows is the message's SENT time (`createdDate`, else the star time when the
 * server sent none); the star time is secondary ("Starred {time}", `chat.group.starred.starredAt`), and only when it
 * falls in a different minute from the sent time. iOS / Android `starredTimes` decide the same.
 */
export function starredTimes(item: Pick<StarredItem, 'createdDate' | 'starredDate'>): { sent: string | null; starred: string | null } {
  const sent = item.createdDate ?? item.starredDate;
  if (!item.createdDate || !item.starredDate) return { sent, starred: null };
  const minute = (iso: string) => {
    const ms = Date.parse(iso);
    return Number.isFinite(ms) ? Math.floor(ms / 60_000) : null;
  };
  const a = minute(item.createdDate);
  const b = minute(item.starredDate);
  return { sent, starred: a !== null && b !== null && a !== b ? item.starredDate : null };
}

/** The starred row's author name: "You", the member's label from that group's summary, else ''. */
export function starredAuthorName(item: Pick<StarredItem, 'author'>, summary: GroupSummary | null, viewerId: string | undefined, t: Translate): string {
  const a = item.author;
  if (!a) return '';
  if (a.kind === 'HUMAN' && viewerId && a.principalMetaSiteId === viewerId) return t('chat.group.you');
  const p = summary ? resolveAuthor(summary, a) : null;
  return p ? participantLabel(p, t) : '';
}

// ─── A row merged in place (realtime `updated`, an action's answer) ─────────────────────────────────────────

/**
 * The transcript with the row of the same id (or persisted finalId) replaced IN PLACE — never appended, never
 * re-sorted. An id that is not loaded is ignored: the same array comes back.
 */
export function mergeUpdatedRow<M extends { id?: string; finalId?: string }>(rows: M[], row: M): M[] {
  if (!row.id) return rows;
  const i = rows.findIndex((m) => m.id === row.id || m.finalId === row.id);
  if (i < 0) return rows;
  const out = rows.slice();
  out[i] = row;
  return out;
}

/** The optimistic row after a reaction: its `groupMeta.reactions` replaced. */
export function withRowReactions<M extends { groupMeta?: GroupMessageMeta }>(row: M, reactions: GroupReaction[]): M {
  const { reactions: _old, ...meta } = row.groupMeta ?? {};
  return { ...row, groupMeta: { ...meta, ...(reactions.length ? { reactions } : {}) } };
}

// ─── Forward ─────────────────────────────────────────────────────────────────────────────────────────────────

/** Whether one more group can be selected (the 6th is refused with `chat.group.forward.limit`). */
export function forwardSelection(selected: readonly string[], id: string): { selected: string[]; limitReached: boolean } {
  if (selected.includes(id)) return { selected: selected.filter((x) => x !== id), limitReached: false };
  if (selected.length >= GROUP_MESSAGE_RULES.maxForwardTargets) return { selected: [...selected], limitReached: true };
  return { selected: [...selected, id], limitReached: false };
}

export interface ForwardSummary {
  /** The toast (null when the call itself failed). */
  toast: { key: string; vars?: Record<string, number> } | null;
  /** chat.group.forward.filesNotForwarded when any target dropped files. */
  filesKey: string | null;
  /** The sheet's own error (the whole call refused or failed): the sheet stays open, the same clientRequestId retries. */
  errorKey: string | null;
}
export function forwardSummary(outcome: GroupActionOutcome): ForwardSummary {
  if (outcome.kind !== 'ok') return { toast: null, filesKey: null, errorKey: messageActionErrorKey('forward', outcome) };
  const results = Array.isArray(outcome.data.results) ? outcome.data.results.filter(isObject) : [];
  const total = results.length;
  const sent = results.filter((r) => !str(r.refusal)).length;
  const files = results.some((r) => typeof r.filesNotForwarded === 'number' && r.filesNotForwarded > 0);
  if (total > 0 && sent === 0) {
    return { toast: null, filesKey: null, errorKey: messageActionErrorKey('forward', { kind: 'refused', refusal: str(results[0].refusal) ?? '', data: {} }) };
  }
  return {
    toast: sent === total ? { key: 'chat.group.forward.done' } : { key: 'chat.group.forward.partial', vars: { sent, total } },
    filesKey: files ? 'chat.group.forward.filesNotForwarded' : null,
    errorKey: null,
  };
}

/** The forward picker's groups: every group but the one forwarded from, filtered by the search text (name contains). */
export function forwardTargets<G extends { conversationId: string; title: string }>(groups: readonly G[], from: string, query: string): G[] {
  const q = query.trim().toLocaleLowerCase();
  return groups.filter((g) => g.conversationId !== from && (!q || g.title.toLocaleLowerCase().includes(q)));
}

// ─── Message info ────────────────────────────────────────────────────────────────────────────────────────────

export interface GroupReceipt {
  metaSiteId: string;
  name: string;
  avatarUrl: string | null;
  readDate: string | null;
  /** Design §21: when their devices received it (never after the read); null from an older server. */
  deliveredDate: string | null;
  /** Design §21: their reads are hidden from the viewer (their switch, the viewer's, a block) — Delivered only. */
  readsHidden: boolean;
}

export function parseReceipts(raw: unknown): GroupReceipt[] {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((r): GroupReceipt[] => {
    if (!isObject(r)) return [];
    const metaSiteId = str(r.metaSiteId);
    if (!metaSiteId) return [];
    const readsHidden = r.readsHidden === true;
    return [{
      metaSiteId,
      name: typeof r.name === 'string' ? r.name : '',
      avatarUrl: str(r.avatarUrl) ?? null,
      readDate: readsHidden ? null : str(r.readDate) ?? null,
      deliveredDate: str(r.deliveredDate) ?? null,
      readsHidden,
    }];
  });
}

/**
 * "Read by" (a visible read date; the latest read first) and "Delivered to" (everyone else, the latest delivery first,
 * then those with none), and whether nobody else can see it. One whose reads are hidden is only ever "Delivered to".
 */
export function splitReceipts(receipts: readonly GroupReceipt[]): { read: GroupReceipt[]; delivered: GroupReceipt[]; empty: boolean } {
  const time = (iso: string | null) => (iso ? Date.parse(iso) || 0 : 0);
  const read = receipts.filter((r) => r.readDate && !r.readsHidden).sort((a, b) => time(b.readDate) - time(a.readDate));
  const delivered = receipts.filter((r) => !r.readDate || r.readsHidden).sort((a, b) => time(b.deliveredDate) - time(a.deliveredDate));
  return { read, delivered, empty: receipts.length === 0 };
}

export type MessageInfoState =
  | { kind: 'loading' }
  | { kind: 'shown'; read: GroupReceipt[]; delivered: GroupReceipt[]; empty: boolean }
  | { kind: 'error'; messageKey: string; retry: boolean };
export function messageInfoState(outcome: GroupActionOutcome): MessageInfoState {
  if (outcome.kind === 'ok') return { kind: 'shown', ...splitReceipts(parseReceipts(outcome.data.receipts)) };
  // A refusal will not change on a retry; a transport failure may.
  return { kind: 'error', messageKey: messageActionErrorKey('info', outcome), retry: outcome.kind !== 'refused' };
}

// ─── Refusal → copy ──────────────────────────────────────────────────────────────────────────────────────────

export type MessageActionKind = 'react' | 'edit' | 'deleteForMe' | 'deleteForEveryone' | 'pin' | 'star' | 'forward' | 'info' | 'starred';

const FAILED_KEYS: Record<MessageActionKind, string> = {
  react: 'chat.group.message.reactFailed',
  edit: 'chat.group.message.editFailed',
  deleteForMe: 'chat.group.message.deleteForMeFailed',
  deleteForEveryone: 'chat.group.message.deleteFailed',
  pin: 'chat.group.message.pinFailed',
  star: 'chat.group.message.starFailed',
  forward: 'chat.group.forward.failed',
  info: 'chat.group.messageInfo.loadFailed',
  starred: 'chat.group.starred.loadFailed',
};

/**
 * The message a non-ok outcome of a Phase 10b action shows: NOT_MESSAGE_AUTHOR reads per action (edit / info /
 * delete); every refusal the shared table knows reads its own key; any other refusal "Try again in a moment"; a
 * transport failure the action's own *Failed key.
 */
export function messageActionErrorKey(action: MessageActionKind, outcome: GroupActionOutcome<unknown>): string {
  if (outcome.kind !== 'refused') return FAILED_KEYS[action];
  if (outcome.refusal === 'NOT_MESSAGE_AUTHOR') {
    if (action === 'edit') return 'chat.group.error.notMessageAuthorEdit';
    if (action === 'info') return 'chat.group.error.notMessageAuthorInfo';
    return 'chat.group.error.notMessageAuthor';
  }
  const key = refusalKey(outcome.refusal);
  return key === 'chat.group.error.generic' ? 'chat.group.error.retryLater' : key;
}

/** Edit: an unchanged (or emptied) text just leaves edit mode; otherwise the trimmed text to send. */
export function editToSend(original: string, edited: string): string | null {
  const text = edited.trim();
  return !text || text === original.trim() ? null : text;
}

/** A receipt's (or a starred row's) time, like the chat's own stamps: today → the time; else the date and the time. */
export function chatStampFormat(iso: string | null, now = new Date()): 'time' | 'dateTime' | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate() ? 'time' : 'dateTime';
}
